import { z, toJsonSchema } from './validation';
import {
  ConsentSchema,
  UserSchema,
  PropertiesSchema,
  EventSchema,
} from './walkeros';
import { ConfigSchema as MappingConfigSchema } from './mapping';
import { Identifier, Timestamp, Counter } from './primitives';
import { LoggerConfigSchema } from './logger';

/**
 * Collector Schemas
 *
 * Mirrors: types/collector.ts
 * Purpose: Runtime validation and JSON Schema generation for collector configurations
 *
 * The collector is the central event processing engine in walkerOS:
 * - Receives events from sources
 * - Processes events with consent and context
 * - Routes events to destinations
 * - Manages session state and globals
 * - Handles lifecycle hooks
 *
 * This file defines schemas for collector configuration, commands, and state management.
 */

// ========================================
// Command Type Schema
// ========================================

/**
 * CommandType - Walker command identifiers
 *
 * Standard commands:
 * - action: TODO - need documentation
 * - config: Update collector configuration
 * - consent: Update consent state
 * - context: TODO - need documentation
 * - destination: Add/update destination
 * - elb: TODO - need documentation
 * - globals: Update global properties
 * - hook: Register lifecycle hook
 * - init: Initialize collector
 * - link: TODO - need documentation
 * - run: Start/restart collector with state
 * - user: Update user data
 * - walker: TODO - need documentation
 *
 * Extensible: allows custom command strings
 */
export const CommandTypeSchema = z
  .union([
    z.enum([
      'action',
      'config',
      'consent',
      'context',
      'destination',
      'elb',
      'globals',
      'hook',
      'init',
      'link',
      'run',
      'user',
      'walker',
    ]),
    z.string(), // Allow custom commands
  ])
  .meta({
    id: 'CollectorCommandType',
    title: 'Collector.CommandType',
    description:
      'Collector command type identifier (standard or extension string).',
  })
  .describe(
    'Collector command type: standard commands or custom string for extensions',
  );

// ========================================
// Configuration Schemas
// ========================================

/**
 * Config - Core collector configuration
 *
 * Controls collector behavior:
 * - run: Auto-run on initialization
 * - globalsStatic: Static globals (persist across runs)
 * - sessionStatic: Static session data (persist across runs)
 * - verbose: Enable verbose logging
 * - onError: Error handler
 * - onLog: Log handler
 */
export const ConfigSchema = z
  .object({
    run: z
      .boolean()
      .describe('Whether to run collector automatically on initialization')
      .optional(),
    globalsStatic: PropertiesSchema.describe(
      'Static global properties that persist across collector runs',
    ),
    sessionStatic: z
      .record(z.string(), z.unknown())
      .meta({
        id: 'CollectorSessionStatic',
        title: 'Collector.SessionStatic',
        description: 'Static session data that persists across collector runs.',
      })
      .describe('Static session data that persists across collector runs'),
    logger: LoggerConfigSchema.optional().describe(
      'Logger configuration (level, handler)',
    ),
    queueMax: z
      .number()
      .optional()
      .describe(
        'Maximum events retained in collector.queue (late-registration replay). FIFO drop on overflow. Default 1000.',
      ),
  })
  .meta({
    id: 'CollectorConfig',
    title: 'Collector.Config',
    description:
      'Core collector configuration (globals/session statics, logger, buffer bounds).',
  })
  .describe('Core collector configuration');

/**
 * SessionData - Session state management
 *
 * Tracks session-level information:
 * - IDs and lifecycle
 * - Storage state
 * - Marketing tracking
 * - Timestamps
 * - Counters
 *
 * Extends Properties to allow custom session data
 */
export const SessionDataSchema = PropertiesSchema.and(
  z.object({
    isStart: z.boolean().describe('Whether this is a new session start'),
    storage: z.boolean().describe('Whether storage is available'),
    id: Identifier.describe('Session identifier').optional(),
    start: Timestamp.describe('Session start timestamp').optional(),
    marketing: z
      .literal(true)
      .optional()
      .describe('Marketing attribution flag'),
    updated: Timestamp.describe('Last update timestamp').optional(),
    isNew: z.boolean().describe('Whether this is a new session').optional(),
    device: Identifier.describe('Device identifier').optional(),
    count: Counter.describe('Event count in session').optional(),
    runs: Counter.describe('Number of runs').optional(),
  }),
)
  .meta({
    id: 'CollectorSessionData',
    title: 'Collector.SessionData',
    description: 'Session state and tracking data.',
  })
  .describe('Session state and tracking data');

/**
 * InitConfig - Initialization configuration
 *
 * Extends Config with initial state:
 * - Initial consent
 * - Initial user data
 * - Initial globals
 * - Source configurations
 * - Destination configurations
 * - Initial custom properties
 */
export const InitConfigSchema = ConfigSchema.partial()
  .extend({
    consent: ConsentSchema.optional().describe('Initial consent state'),
    user: UserSchema.optional().describe('Initial user data'),
    globals: PropertiesSchema.optional().describe('Initial global properties'),
    // Sources, destinations, transformers, stores are complex runtime objects
    sources: z
      .unknown()
      .meta({
        id: 'CollectorInitSources',
        title: 'Source.InitSources',
        description: 'Source configurations map (id → InitSource).',
      })
      .optional()
      .describe('Source configurations'),
    destinations: z
      .unknown()
      .meta({
        id: 'CollectorInitDestinations',
        title: 'Destination.InitDestinations',
        description: 'Destination configurations map (id → Init).',
      })
      .optional()
      .describe('Destination configurations'),
    transformers: z
      .unknown()
      .meta({
        id: 'CollectorInitTransformers',
        title: 'Transformer.Configs',
        description: 'Transformer configurations map (id → Config).',
      })
      .optional()
      .describe('Transformer configurations'),
    stores: z
      .unknown()
      .meta({
        id: 'CollectorInitStores',
        title: 'Store.Configs',
        description: 'Store configurations map (id → Config).',
      })
      .optional()
      .describe('Store configurations'),
    custom: PropertiesSchema.optional().describe(
      'Initial custom implementation-specific properties',
    ),
    hooks: z
      .unknown()
      .meta({
        id: 'CollectorHooks',
        title: 'Collector.Hooks',
        description: 'Pipeline observation hooks.',
      })
      .optional()
      .describe('Pipeline observation hooks'),
  })
  .meta({
    id: 'CollectorInitConfig',
    title: 'Collector.InitConfig',
    description:
      'Collector initialization configuration with initial state (consent, user, globals, components, hooks).',
  })
  .describe('Collector initialization configuration with initial state');

// ========================================
// Context Schemas
// ========================================

/**
 * PushContext - Context for collector.push
 *
 * Provides source-level mapping configuration
 * Applied before destination-specific mappings
 */
export const PushContextSchema = z
  .object({
    mapping: MappingConfigSchema.optional().describe(
      'Source-level mapping configuration',
    ),
  })
  .meta({
    id: 'CollectorPushContext',
    title: 'Collector.PushContext',
    description: 'Push context with optional source-level mapping.',
  })
  .describe('Push context with optional source mapping');

// ========================================
// Collection Schemas
// ========================================

/**
 * Sources - Map of source IDs to instances
 */
export const SourcesSchema = z
  .record(z.string(), z.unknown())
  .meta({
    id: 'CollectorSources',
    title: 'Collector.Sources',
    description: 'Map of source IDs to runtime source instances.',
  })
  .describe('Map of source IDs to source instances');

/**
 * Destinations - Map of destination IDs to instances
 */
export const DestinationsSchema = z
  .record(z.string(), z.unknown())
  .meta({
    id: 'CollectorDestinations',
    title: 'Collector.Destinations',
    description: 'Map of destination IDs to runtime destination instances.',
  })
  .describe('Map of destination IDs to destination instances');

// ========================================
// Instance Schema
// ========================================

/**
 * Instance - Collector instance (runtime object)
 *
 * The main collector interface with all state and methods
 *
 * State:
 * - config: Current configuration
 * - consent: Current consent state
 * - user: Current user data
 * - globals: Current global properties
 * - custom: Custom properties
 * - session: Session state
 * - sources: Registered sources
 * - destinations: Registered destinations
 * - hooks: Lifecycle hooks
 * - on: Event lifecycle config
 * - queue: Queued events
 *
 * Flags:
 * - allowed: Processing allowed
 * - count: Event count
 * - round: Collector run count
 * - timing: Processing timing
 * - group: Event grouping ID
 *
 * Methods (not validated):
 * - push: Process events
 * - command: Execute commands
 */
export const InstanceSchema = z
  .object({
    // Methods (functions - not validated)
    push: z.unknown().describe('Push function for processing events'),
    command: z.unknown().describe('Command function for walker commands'),
    // State
    allowed: z.boolean().describe('Whether event processing is allowed'),
    config: ConfigSchema.describe('Current collector configuration'),
    consent: ConsentSchema.describe('Current consent state'),
    custom: PropertiesSchema.describe(
      'Custom implementation-specific properties',
    ),
    sources: SourcesSchema.describe('Registered source instances'),
    destinations: DestinationsSchema.describe(
      'Registered destination instances',
    ),
    globals: PropertiesSchema.describe('Current global properties'),
    hooks: z.unknown().describe('Lifecycle hook functions'),
    on: z.unknown().describe('Event lifecycle configuration'),
    queue: z.array(EventSchema).describe('Queued events awaiting processing'),
    round: z
      .number()
      .describe('Collector run count (increments with each run)'),
    session: z.union([SessionDataSchema]).describe('Current session state'),
    timing: z.number().describe('Event processing timing information'),
    user: UserSchema.describe('Current user data'),
  })
  .meta({
    id: 'CollectorInstance',
    title: 'Collector.Instance',
    description:
      'Collector instance (runtime object with push/command + full state and component maps).',
  })
  .describe('Collector instance with state and methods');

// ========================================
// JSON Schema Exports (for Explorer/RJSF/MCP)
// ========================================

export const commandTypeJsonSchema = toJsonSchema(
  CommandTypeSchema,
  'CommandType',
);

export const configJsonSchema = toJsonSchema(ConfigSchema, 'CollectorConfig');

export const sessionDataJsonSchema = toJsonSchema(
  SessionDataSchema,
  'SessionData',
);

export const initConfigJsonSchema = toJsonSchema(
  InitConfigSchema,
  'InitConfig',
);

export const pushContextJsonSchema = toJsonSchema(
  PushContextSchema,
  'CollectorPushContext',
);

export const instanceJsonSchema = toJsonSchema(
  InstanceSchema,
  'CollectorInstance',
);
